/* eslint-disable no-restricted-imports */
import React, { useCallback, useState } from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'
import {
  not,
  omit,
  pick,
  values,
} from 'ramda'

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import {
  ButtonBase,
  IconButton,
  ImageList,
  ImageListItem,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import createLogger from '~/src/Lib/Logging'
import { datePropType } from '~/src/Lib/PropTypes'
import { goldenth, memoize } from '~/src/Lib/Utils'

import {
  getCalendarDays,
  getItemCountForDay,
  getSetSelectedDate,
  isEnd,
} from './utils'

const getTileClasses = memoize((classes, name, keys) => ({
  [name]: classNames(values(pick(keys, classes)))
}))

const displayName = 'Journal/Calendar'
const logger = createLogger(displayName)

const collapseByDefaultQuery = '@media (max-width: 959.5px), (max-height: 600px)'

const styles = theme => ({
  root: {
    margin: 0,
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    '&$expanded': {
      borderBottom: 'none',
    },
    '&$expanded $expandIcon': {
      transform: 'rotate(180deg)'
    },
    [collapseByDefaultQuery]: {
      padding: '0 calc((100% - 390px) / 2)',
      margin: '0 auto !important',
      height: 36,
      overflowY: 'hidden',
      transition: theme.transitions.create(['height']),
      '&$expanded': {
        height: 'auto'
      },
      '& $paper': {
        height: 32,
      },
      '&$sixWeeks $paper': {
        height: 28,
      },
      '& $nextPrev': {
        transition: theme.transitions.create(['opacity']),
        opacity: 0
      },
      '&$expanded $nextPrev': {
        opacity: 1
      }
    },
  },
  expanded: {},
  sixWeeks: {},
  expandIcon: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create(['transform']),
  },
  paper: {
    borderRadius: 0,
    width: '100%',
    height: 36,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    color: theme.palette.primary.main,
    background: 'none',
    transition: 'background, color 150ms ease-in-out',
    textAlign: 'center',
    '& > p': {
      color: 'inherit',
      fontFamily: theme.typography.boldFontFamily,
    },
    '&$cell:hover, &$today': {
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.primary.active,
      cursor: 'pointer',
    },
    '&$selectedDate, &$selectedDate:hover': {
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.primary.main,
      '& > p, &:before': {
        color: theme.palette.text.primary,
        opacity: 1,
      },
    }
  },
  currentMonthHeader: {
    width: 'auto',
    textTransform: 'uppercase',
    fontSize: '14px',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      marginTop: 0,
    }
  },
  monthRow: {
    height: 36,
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(),
      height: 28
    }
  },
  nextPrev: {
    textAlign: 'center',
  },
  nav: {
    '& button': {
      margin: '0 auto',
      width: 32,
    }
  },
  dateHeader: {
    '& > p': {
      color: theme.palette.gray,
    },
  },
  cell: {},
  tile: {
    width: '100%',
  },
  offMonth: {
    '& > p': {
      color: theme.palette.gray,
      opacity: goldenth(),
    },
  },
  today: {},
  selectedDate: {},
  hasItems: {
    '&:before': {
      position: 'absolute',
      bottom: 4,
      width: '100%',
      textAlign: 'center',
      fontSize: '1.5em',
      content: '"."'
    },
    '&[data-itemdots="2"]:before': {
      content: '".."'
    },
    '&[data-itemdots="3"]:before': {
      content: '"…"'
    },
    [theme.breakpoints.down('md')]: {
      '&:before': {
        fontSize: '1.1em',
        bottom: 1
      }
    }
  },
})

const useStyles = makeStyles(styles, { name: displayName })

const dayPassthruCleaner = omit(['dispatch', 'isoDate'])

const Day = React.memo(props => {
  const {
    classes,
    calendarAnchorDate,
    day,
    journal,
    loading,
    selectedDate,
    today,
    onClickCallback,
    ...passthruProps
  } = props
  const itemCount = getItemCountForDay(day, journal)

  let dots = itemCount ? 1 : 0
  if (itemCount > 5 && itemCount <= 10) {
    dots = 2
  } else if (itemCount > 10) {
    dots = 3
  }

  return (
    <ImageListItem
      cols={1}
      component={ButtonBase}
      onClick={getSetSelectedDate(props, day, onClickCallback)}
      disabled={loading || !itemCount}
      classes={getTileClasses(classes, 'tile', ['tile'])}
      {...dayPassthruCleaner(passthruProps)}
    >
      <Paper
        elevation={0}
        classes={getTileClasses(classes, 'root', ['paper'])}
        className={classNames({
          [classes.cell]: true,
          [classes.offMonth]: day.month !== calendarAnchorDate.month,
          [classes.today]: +day === +today,
          [classes.selectedDate]: day.hasSame(selectedDate, 'day'),
          [classes.hasItems]: !!itemCount,
        })}
        data-itemdots={dots}
      >
        <Typography component="span" variant="body2">{day.toFormat('d')}</Typography>
      </Paper>
    </ImageListItem>
  )
}, (prevProps, nextProps) => {
  const {
    calendarAnchorDate: prevCalDate,
    selectedDate: prevSelected,
    journal: {
      items: prevItems
    }
  } = prevProps
  const {
    calendarAnchorDate: nextCalDate,
    selectedDate: nextSelected,
    journal: {
      items: nextItems
    }
  } = nextProps
  if (
    nextItems !== prevItems
    || nextSelected !== prevSelected
    || nextCalDate !== prevCalDate
  ) {
    return false
  }
  return true
})
Day.propTypes = {
  calendarAnchorDate: datePropType.isRequired,
  day: datePropType.isRequired,
  journal: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      localTimestamp: datePropType,
    })),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  selectedDate: datePropType.isRequired,
  today: datePropType.isRequired,
  onClickCallback: PropTypes.func.isRequired,
}

export const JournalCalendarComponent = ({
  calendarEndDate,
  calendarStartDate,
  dispatch,
  goToNextMonth,
  goToPrevMonth,
  journal,
  loading,
  selectedDate,
  today,
}) => {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false)
  const toggleExpanded = useCallback(() => setExpanded(not), [])
  const days = getCalendarDays({ calendarStartDate, calendarEndDate })
  const { [Math.floor(days.length / 2)]: calendarAnchorDate } = days
  const isCollapsedByDefault = useMediaQuery(collapseByDefaultQuery)
  const goToPrevMonthDisabled = loading || isEnd('start', { calendarStartDate, calendarEndDate, journal })
  const goToNextMonthDisabled = loading || isEnd('end', { calendarStartDate, calendarEndDate, journal })

  return (
    <ImageList
      data-testid="facility-dashboard-journal-calendar"
      cellHeight="auto"
      cols={7}
      gap={0}
      className={classNames({
        [classes.root]: true,
        [classes.expanded]: expanded,
        [classes.sixWeeks]: days.length > 35,
      })}
    >
      {isCollapsedByDefault ? <ImageListItem cols={1} /> : null}
      <ImageListItem cols={1}>
        <Paper classes={getTileClasses(classes, 'root', ['paper', 'nav', 'nextPrev'])}>
          <IconButton
            color="primary"
            disabled={goToPrevMonthDisabled}
            size="small"
            onClick={goToPrevMonth}
          >
            <ChevronLeftIcon color="inherit" />
          </IconButton>
        </Paper>
      </ImageListItem>
      <ImageListItem cols={isCollapsedByDefault ? 3 : 5}>
        <Paper classes={getTileClasses(classes, 'root', ['paper', 'currentMonthHeader'])}>
          {isCollapsedByDefault && !expanded
            ? selectedDate.toFormat('LLLL d, yyyy')
            : calendarAnchorDate.toFormat('LLLL yyyy')}
        </Paper>
      </ImageListItem>
      <ImageListItem cols={1}>
        <Paper classes={getTileClasses(classes, 'root', ['paper', 'nav', 'nextPrev'])}>
          <IconButton
            color="primary"
            disabled={goToNextMonthDisabled}
            size="small"
            onClick={goToNextMonth}
          >
            <ChevronRightIcon color="inherit" />
          </IconButton>
        </Paper>
      </ImageListItem>
      {isCollapsedByDefault ? (
        <ImageListItem cols={1}>
          <Paper classes={getTileClasses(classes, 'root', ['paper', 'nav'])}>
            <IconButton
              className={classes.expandIcon}
              color="primary"
              disabled={loading}
              size="small"
              onClick={toggleExpanded}
            >
              <ExpandMoreIcon color="inherit" />
            </IconButton>
          </Paper>
        </ImageListItem>
      ) : null}
      {days.slice(0, 7).map(day => {
        const dateHeader = day.toFormat('ccccc')
        return (
          <ImageListItem key={`${day}-{header}`} className={classes.title}>
            <Paper
              elevation={0}
              classes={getTileClasses(classes, 'root', ['paper'])}
              className={classes.dateHeader}
            >
              <Typography variant="body2" color="primary">{dateHeader}</Typography>
            </Paper>
          </ImageListItem>
        )
      })}

      {days.map(day => (
        <Day
          key={day.toISODate()}
          classes={classes}
          calendarAnchorDate={calendarAnchorDate}
          day={day}
          dispatch={dispatch}
          journal={journal}
          loading={loading}
          selectedDate={selectedDate}
          today={today}
          onClickCallback={toggleExpanded}
        />
      ))}
    </ImageList>
  )
}

JournalCalendarComponent.propTypes = {
  calendarEndDate: datePropType.isRequired,
  calendarStartDate: datePropType.isRequired,
  dispatch: PropTypes.func.isRequired,
  goToNextMonth: PropTypes.func.isRequired,
  goToPrevMonth: PropTypes.func.isRequired,
  journal: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      data: PropTypes.oneOfType([PropTypes.object, PropTypes.symbol]),
      timestamp: datePropType,
    })),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  selectedDate: datePropType.isRequired,
  today: datePropType.isRequired,
}

export default React.memo(JournalCalendarComponent)
/* eslint-enable no-restricted-imports */
